/* XS product page - licence eyebrow, vendor byline, length-aware description.

   The title is a theme block whose markup we do not own (templates/product.json
   stores it as `<h1>{{ closest.product.title }}</h1>`, and a text block only
   evaluates dynamic sources, not real Liquid), so anything that has to sit
   immediately above or below it is inserted here. The values themselves are
   resolved server-side in sections/xs-product-assets.liquid and handed over on
   window - no client-side guessing about which licence or supplier a product
   belongs to.

   No requestAnimationFrame anywhere: it is throttled to zero in a background
   tab, so a shopper opening a product in a new tab would come back to an
   unstyled eyebrow and an unclamped 4,000px wall of text. Timers keep firing.
*/
(function () {
  var SECTION = '[id$="__section_JPWWmH"]';
  var CLAMP = 420;
  var SLACK = 140;

  /* ---------------------------------------------------------- licence -- */
  /* PRIMARY source is window.__xsLicence, resolved in Liquid from the product's
     COLLECTION membership by sections/xs-product-assets.liquid. That is the only
     reliable signal - an NBA sock is titled "Atlanta Hawks - #1 Dad" and tagged
     only [Atlanta Hawks], with no league anywhere on the product record.

     The table below is a FALLBACK for products outside a licence collection that
     still name a licence in the title. Order matters: "star wars" before the
     bare league checks, and DC only via its characters, because the letters
     "dc" appear inside ordinary words. */
  var LICENCES = [
    ['nfl', 'NFL', '#4C8DFF'],
    ['ncaa', 'NCAA', '#FF7A33'],
    ['mlb', 'MLB', '#FF5A52'],
    ['nba', 'NBA', '#FF9F1C'],
    ['nhl', 'NHL', '#35D0EE'],
    ['mls', 'MLS', '#3DDC84'],
    ['star wars', 'Star Wars', '#FFD54A'],
    ['star trek', 'Star Trek', '#FFC24A'],
    ['harry potter', 'Harry Potter', '#B07CFF'],
    ['game of thrones', 'Game of Thrones', '#9FB4C7'],
    ['minecraft', 'Minecraft', '#3DDC84'],
    ['coca-cola', 'Coca-Cola', '#FF5A52'],
    ['disney', 'Disney', '#5AC8FF'],
    ['marvel', 'Marvel', '#FF5A52'],
    ['batman', 'DC Comics', '#4C8DFF'],
    ['superman', 'DC Comics', '#4C8DFF'],
    ['dc comics', 'DC Comics', '#4C8DFF']
  ];

  function fromTitle(title) {
    var t = (title || '').toLowerCase();
    for (var i = 0; i < LICENCES.length; i++) {
      if (t.indexOf(LICENCES[i][0]) > -1) {
        return { name: LICENCES[i][1], accent: LICENCES[i][2] };
      }
    }
    return null;
  }

  function brand() {
    var h1 = document.querySelector('.product-details h1');
    if (!h1 || h1.dataset.xsBranded === '1') return;

    var hit = null;
    if (window.__xsLicence) {
      hit = { name: window.__xsLicence, accent: window.__xsAccent || '#FFB020' };
    } else {
      hit = fromTitle(h1.textContent);
    }

    var accent = hit ? hit.accent : (window.__xsAccent || '#FFB020');
    var root = document.querySelector('[id$="__main"]') || document.body;
    root.style.setProperty('--xs-cat', accent);
    document.documentElement.style.setProperty('--xs-cat', accent);

    h1.dataset.xsBranded = '1';

    /* VENDOR BYLINE, directly under the title.

       __xsVendor arrives already filtered: the Liquid blanks out the suppliers
       that are not brands worth crediting, so an empty string here means "this
       one is deliberately unattributed", not "lookup failed". Nothing to decide
       client-side, and the deny list stays in one place.

       Written as "By Dango Products" and upper-cased in CSS rather than in the
       string, so what a screen reader announces and what a shopper copies is
       ordinary prose.

       Inserted before the early return below - a product can have a vendor
       worth crediting and no licence at all, which is true of every Knockaround
       and every GRIP6. */
    var vend = window.__xsVendor;
    if (vend && h1.parentNode && !h1.parentNode.querySelector('.xsp-vendor')) {
      var byline = document.createElement('p');
      byline.className = 'xsp-vendor';
      byline.textContent = 'By ' + vend;
      h1.parentNode.insertBefore(byline, h1.nextSibling);
    }

    if (!hit || !hit.name) return;

    var eyebrow = document.createElement('p');
    eyebrow.className = 'xsp-licence';
    eyebrow.textContent = hit.name + ' · Officially licensed';
    h1.parentNode.insertBefore(eyebrow, h1);
  }

  /* ------------------------------------------------------ description -- */
  function setup(section) {
    if (!section || section.dataset.xspReady === '1') return;
    var rte = section.querySelector('rte-formatter');
    if (!rte) return;

    section.classList.add('xsp-desc');
    section.dataset.xspReady = '1';

    /* The block template hard-codes a "Product Description" heading, so a
       product with no copy still has text in it. Ignore the heading. */
    var probe = rte.cloneNode(true);
    var heading = probe.querySelector('h4');
    if (heading) heading.remove();
    var hasText = (probe.textContent || '').replace(/\\s+/g, '').length > 0;
    var hasMedia = !!rte.querySelector('img, iframe, video, table');

    if (!hasText && !hasMedia) {
      section.classList.add('xsp-desc--empty');
      return;
    }

    var body = document.createElement('div');
    body.className = 'xsp-desc__body';
    rte.parentNode.insertBefore(body, rte);
    body.appendChild(rte);

    measure(section, body);

    var imgs = rte.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) {
        imgs[i].addEventListener('load', function () { measure(section, body); });
      }
    }
    window.setTimeout(function () { measure(section, body); }, 900);
  }

  function measure(section, body) {
    if (section.classList.contains('xsp-desc--open')) return;
    var full = body.scrollHeight;

    if (full <= CLAMP + SLACK) {
      section.classList.remove('xsp-desc--clamped');
      var stale = section.querySelector('.xsp-desc__toggle');
      if (stale) stale.remove();
      return;
    }

    section.classList.add('xsp-desc--clamped');
    if (section.querySelector('.xsp-desc__toggle')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'xsp-desc__toggle';
    btn.setAttribute('aria-expanded', 'false');
    if (!body.id) body.id = 'xsp-desc-body';
    btn.setAttribute('aria-controls', body.id);
    btn.textContent = 'Read full details';

    btn.addEventListener('click', function () {
      var open = section.classList.toggle('xsp-desc--open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? 'Show less' : 'Read full details';
      if (!open) {
        var top = section.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    });

    body.parentNode.insertBefore(btn, body.nextSibling);
  }

  function run() {
    brand();
    var s = document.querySelector(SECTION);
    if (s) setup(s);
  }

  var timer = null;
  function schedule() {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(function () { timer = null; run(); }, 40);
  }

  schedule();
  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('load', schedule);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('resize', function () {
    var s = document.querySelector(SECTION);
    var b = s && s.querySelector('.xsp-desc__body');
    if (s && b) measure(s, b);
  });
})();